/**
 * Ein kleiner, einheitlicher Satz Umriss-Symbole (16 px, 1,5 px Strich).
 *
 * Bewusst inline statt als Bibliothek: Das Panel braucht nur eine Handvoll
 * Symbole, und so erben sie Farbe (`currentColor`) und Größe direkt vom Text.
 */

const PATHS = {
  chevronRight: "M6 3.5 10.5 8 6 12.5",
  chevronLeft: "M10 3.5 5.5 8 10 12.5",
  chevronDown: "M3.5 6 8 10.5 12.5 6",
  arrowRight: "M3 8h10M9 4l4 4-4 4",
  check: "M3.5 8.5 6.5 11.5 12.5 4.5",
  info: "M8 7.25v4M8 4.75v.01M14.25 8A6.25 6.25 0 1 1 1.75 8a6.25 6.25 0 0 1 12.5 0Z",
  bulb: "M6 12.25h4M6.5 14.25h3M5.25 9.5a4.25 4.25 0 1 1 5.5 0c-.5.45-.75 1-.75 1.6v.15H6v-.15c0-.6-.25-1.15-.75-1.6Z",
  code: "M5.5 4.5 2 8l3.5 3.5M10.5 4.5 14 8l-3.5 3.5",
  task: "M5.75 2.75h-2a1 1 0 0 0-1 1v9.5a1 1 0 0 0 1 1h8.5a1 1 0 0 0 1-1v-9.5a1 1 0 0 0-1-1h-2M5.75 2h4.5v2h-4.5zM5.5 9l1.75 1.75L10.5 7.5",
  target: "M8 14.25A6.25 6.25 0 1 0 8 1.75a6.25 6.25 0 0 0 0 12.5ZM8 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z",
  clock: "M8 4.75V8l2 1.5M14.25 8A6.25 6.25 0 1 1 1.75 8a6.25 6.25 0 0 1 12.5 0Z",
  copy: "M5.5 5.5V3.25a1 1 0 0 1 1-1h6.25a1 1 0 0 1 1 1V9.5a1 1 0 0 1-1 1H10.5M3.25 5.5H9.5a1 1 0 0 1 1 1v6.25a1 1 0 0 1-1 1H3.25a1 1 0 0 1-1-1V6.5a1 1 0 0 1 1-1Z",
  alert: "M8 5.75v3M8 11v.01M7.13 2.5 1.6 12a1 1 0 0 0 .87 1.5h11.06a1 1 0 0 0 .87-1.5L8.87 2.5a1 1 0 0 0-1.74 0Z",
  reset: "M2.75 8a5.25 5.25 0 1 0 1.54-3.71M2.75 2.75v2.5h2.5",
  search: "M7.25 12.25a5 5 0 1 0 0-10 5 5 0 0 0 0 10ZM13.75 13.75l-3-3",
  menu: "M2.5 4h11M2.5 8h11M2.5 12h11",
  close: "M4 4l8 8M12 4l-8 8",
  home: "M2.5 7 8 2.5 13.5 7v6.25a.75.75 0 0 1-.75.75H9.5v-4h-3v4H3.25a.75.75 0 0 1-.75-.75Z",
  book: "M8 4.25C6.75 3.25 4.75 2.75 2.5 2.75v9.5c2.25 0 4.25.5 5.5 1.5 1.25-1 3.25-1.5 5.5-1.5v-9.5c-2.25 0-4.25.5-5.5 1.5ZM8 4.25v9.5",
} as const;

export type IconName = keyof typeof PATHS;

interface IconProps {
  name: IconName;
  size?: number;
  className?: string;
}

export function Icon({ name, size = 16, className }: IconProps) {
  return (
    <svg
      className={`icon icon--${name}${className ? ` ${className}` : ""}`}
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {/* pathLength 1 erlaubt, ein Symbol per CSS „zeichnen“ zu lassen. */}
      <path d={PATHS[name]} pathLength={1} />
    </svg>
  );
}

/**
 * Zeichen des Produkts: ein Blatt mit Eselsohr und Haken – das Add-in prüft,
 * was im Dokument daneben passiert. Es nimmt die Farbe der Host-Anwendung an.
 */
export function BrandMark() {
  return (
    <svg className="brand-mark" width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
      <path
        d="M4.5 2.5h7.25L15.5 6.25V16.5a1 1 0 0 1-1 1h-10a1 1 0 0 1-1-1v-13a1 1 0 0 1 1-1Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="M11.5 2.75V6.5h3.75" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path
        d="m6.75 11.5 2 2 4-4.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
